import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';

const PrivacyPolicy = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  return (
    <div className="privacy-policy-page">
      {/* Header Navigation */}
      <header className={`landing-header ${isVisible ? 'header-visible' : ''}`}>
        <div className="header-content">
          <div className={`logo ${isVisible ? 'logo-visible' : ''}`} onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <span className="logo-text">UniPostings</span>
          </div>
          <nav className={`nav-menu ${isVisible ? 'nav-visible' : ''}`}>
            <Button 
              label="Back to Home" 
              className="header-cta"
              onClick={() => navigate('/')}
            />
          </nav>
        </div>
      </header>

      {/* Privacy Policy Content */}
      <div className={`privacy-content ${isVisible ? 'content-visible' : ''}`}>
        <div className="privacy-container">
          <h1 className="privacy-title">Privacy Policy</h1>
          
          <div className="privacy-meta">
            <p><strong>Effective Date:</strong> November 11, 2025</p>
            <p><strong>Last Updated:</strong> November 11, 2025</p>
          </div>

          <div className="privacy-intro">
            <p>
              Welcome to UniPostings ("we," "our," or "us"). We value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and share information when you use UniPostings.com, our submission forms, or any associated social media accounts (collectively, the "Service").
            </p>
          </div>

          {/* Section 1 */}
          <section className="privacy-section">
            <h2>1. Information We Collect</h2>
            <p>We collect the following types of information when you use UniPostings:</p>
            
            <div className="privacy-subsection">
              <h3>🔹 Information You Provide Directly</h3>
              <p>When you submit your bio or request to be posted, we may collect:</p>
              <ul>
                <li>Name</li>
                <li>Email address or social handle</li>
                <li>Profile photo(s)</li>
                <li>University name and graduating class (e.g., Class of 2030)</li>
                <li>Major, interests, or short bio</li>
                <li>Consent to publish your post</li>
              </ul>
            </div>

            <div className="privacy-subsection">
              <h3>🔹 Automatically Collected Information</h3>
              <p>When you visit our website or interact with our forms, we may automatically collect:</p>
              <ul>
                <li>Device and browser information</li>
                <li>IP address and approximate location</li>
                <li>Date and time of submissions or site visits</li>
                <li>Cookies and analytics data to improve performance and user experience</li>
              </ul>
            </div>

            <div className="privacy-subsection">
              <h3>🔹 Information from Third Parties</h3>
              <p>
                If you interact with UniPostings through third-party services (for example, Instagram or Stripe), we may receive information from those services consistent with their policies and your privacy settings.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="privacy-section">
            <h2>2. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Publish your bio and photo on your school's UniPostings account or page</li>
              <li>Display your post so other students can find and connect with you</li>
              <li>Contact you regarding your submission or updates to our Service</li>
              <li>Improve and personalize your experience</li>
              <li>Analyze engagement and website traffic</li>
              <li>Comply with legal and regulatory obligations</li>
            </ul>
            <p className="privacy-highlight">We do not sell your personal information.</p>
          </section>

          {/* Section 3 */}
          <section className="privacy-section">
            <h2>3. How We Share Information</h2>
            <p>We may share information in the following limited cases:</p>
            <ul>
              <li><strong>With your consent:</strong> When you agree to have your post publicly shared.</li>
              <li><strong>With service providers:</strong> Trusted vendors that host our website, analytics tools, and payment processors (e.g., Stripe, Vercel).</li>
              <li><strong>For legal reasons:</strong> If required by law or to protect our rights, property, or safety.</li>
            </ul>
            <p className="privacy-note">
              Your public post (photo, name, bio, etc.) will be visible to anyone visiting the UniPostings social media accounts or website.
            </p>
          </section>

          {/* Section 4 */}
          <section className="privacy-section">
            <h2>4. Data Retention</h2>
            <p>
              We retain your personal data for as long as necessary to provide our Service. You may request removal of your post or deletion of your data at any time by emailing{' '}
              <a href="mailto:privacy@unipostings.com" className="privacy-link">privacy@unipostings.com</a>.
            </p>
          </section>

          {/* Section 5 */}
          <section className="privacy-section">
            <h2>5. Your Rights</h2>
            <p>Depending on your location, you may have rights to:</p>
            <ul>
              <li>Access, correct, or delete your personal information</li>
              <li>Withdraw consent to have your information posted</li>
              <li>Request a copy of the data we hold about you</li>
            </ul>
            <p>
              To exercise these rights, contact us at{' '}
              <a href="mailto:hello@unipostings.com" className="privacy-link">hello@unipostings.com</a>.
            </p>
          </section>

          {/* Section 6 */}
          <section className="privacy-section">
            <h2>6. Cookies and Tracking</h2>
            <p>
              We use cookies and analytics tools (such as Google Analytics) to understand how users interact with our website. You can disable cookies in your browser settings if you prefer.
            </p>
          </section>

          {/* Section 7 */}
          <section className="privacy-section">
            <h2>7. Security</h2>
            <p>
              We use reasonable administrative, technical, and physical safeguards to protect your data. However, no system is completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Section 8 */}
          <section className="privacy-section">
            <h2>8. Children's Privacy</h2>
            <p>
              UniPostings is intended for college students aged 17 and older. We do not knowingly collect data from minors under 13. If you believe we have inadvertently collected such data, please contact us immediately.
            </p>
          </section>

          {/* Section 9 */}
          <section className="privacy-section">
            <h2>9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The "Last Updated" date will reflect the most recent changes. Continued use of the Service after updates means you accept the new policy.
            </p>
          </section>

          {/* Section 10 */}
          <section className="privacy-section">
            <h2>10. Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy or our practices, contact us at:</p>
            <div className="contact-info">
              <p><strong>UniPostings Privacy Team</strong></p>
              <p>📧 <a href="mailto:hello@unipostings.com" className="privacy-link">hello@unipostings.com</a></p>
              <p>🌐 <a href="https://www.unipostings.com" className="privacy-link" target="_blank" rel="noopener noreferrer">www.unipostings.com</a></p>
            </div>
          </section>

          {/* Back to Home Button */}
          <div className="privacy-footer-cta">
            <Button 
              label="Back to Home" 
              className="privacy-cta-button"
              onClick={() => navigate('/')}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>Contact Us</h4>
              <p>hello@UniPostings.com</p>
              <p>Questions? We're here to help!</p>
            </div>
            <div className="footer-section">
              <h4>Legal</h4>
              <p><a href="/privacy-policy" className="footer-link">Privacy Policy</a></p>
              <p><a href="/terms-of-service" className="footer-link">Terms of Service</a></p>
            </div>
            <div className="footer-section">
              <h4>Follow Us</h4>
              <p>@UniPostings on Instagram</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 UniPostings LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;

